/**
 * UI shell + controller: builds the app chrome, owns the engine clients and
 * import/export flows, and hosts the three workflow panels. Also exports the
 * small DOM/control builder library the panels compose their forms from.
 */
import type {
  CutParams,
  LayerInfo,
  PhotoFillSettings,
  PhotoResult,
  PhotoSettings,
  PixelSettings,
  PolyGeom,
  RawImage,
  VectorDocument,
  VectorExportContext,
} from "../core/types";
import type { UnitSystem } from "../core/types";
import { APP_NAME, APP_VERSION, DEFAULT_PIXEL_SETTINGS, Q_PULSE_STEPS } from "../core/types";
import { nearestQPulseStep } from "./presets";
import { scanAnglePreviewSVG } from "./scanPreview";
import {
  buildPhotoResultFromFill,
  createInitialState,
  decodeImageFile,
  exportReady,
  decodeTextSniffed,
  fileToBytes,
  formatDim,
  mmToUnit,
  parseLocaleNumber,
  photoPaletteLayerColors,
  photoResultToPixels,
  photoSignature,
  savePersisted,
  Store,
  UNIT_LABEL,
  unitToMm,
  vectorSignature,
  type AppState,
  type TabId,
} from "./state";
import { openAboutModal } from "./about";
import { openHelpModal } from "./help";
import { parseLbrn } from "../formats/lbrn";
import { parseSvgVector } from "../formats/svgImport";
import { PixelateClient, type JobProgress } from "../workers/pixelateClient";
import { PhotoClient } from "../workers/photoClient";
import { exportVectorSvg as buildVectorSvg, exportPhotoSvg as buildPhotoSvg } from "../export/svgExport";
import { exportPaletteCsv as buildPaletteCsv, buildPaletteReportHtml } from "../export/paletteCsv";
import { deriveFileName, saveTextFile } from "../export/download";
import { exportAllPhotoBundle, exportAllVectorBundle } from "../export/zipWriter";
import {
  exportLbrnCopy,
  exportLbrnStandalone,
  exportTestGrid as buildTestGrid,
} from "../formats/lbrnExport";
import { CanvasView } from "./canvasView";
import { ProgressOverlay } from "./progress";
import { initToasts, toast, type ToastKind } from "./toasts";
import { createVectorPanel } from "./panels/vectorPanel";
import { createPhotoPanel } from "./panels/photoPanel";
import { createToolsPanel } from "./panels/toolsPanel";

/** Base-URL-safe path to a bundled public asset (respects Vite `base`). */
const FAVICON_URL = import.meta.env.BASE_URL + "favicon.svg";

// ===========================================================================
// DOM + control builder library (used by every panel).
// ===========================================================================

let uid = 0;
function nextId(): string {
  return "c" + ++uid;
}

export interface ElOpts {
  class?: string;
  text?: string;
  html?: string;
  title?: string;
  attrs?: Record<string, string>;
  onClick?: (e: MouseEvent) => void;
}

export function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  opts: ElOpts = {},
  children: (Node | string)[] = [],
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (opts.class) node.className = opts.class;
  if (opts.text !== undefined) node.textContent = opts.text;
  if (opts.html !== undefined) node.innerHTML = opts.html;
  if (opts.title) node.title = opts.title;
  if (opts.attrs) for (const [k, v] of Object.entries(opts.attrs)) node.setAttribute(k, v);
  if (opts.onClick) node.addEventListener("click", opts.onClick as EventListener);
  for (const c of children) node.append(c);
  return node;
}

export function clearNode(node: Node): void {
  while (node.firstChild) node.removeChild(node.firstChild);
}

export interface Field<T> {
  root: HTMLElement;
  get(): T;
  set(v: T): void;
}

export interface SectionHandle {
  root: HTMLDetailsElement;
  content: HTMLDivElement;
  setBadge(text: string | null): void;
}

export function section(title: string, opts: { open?: boolean; hint?: string } = {}): SectionHandle {
  const badge = el("span", { class: "count-badge" });
  badge.style.display = "none";
  const chevron = el("span", { class: "chevron", text: "▶" });
  const summary = el("summary", {}, [title, badge, chevron]);
  const content = el("div", { class: "section-content" });
  if (opts.hint) content.append(el("p", { class: "section-hint", text: opts.hint }));
  const root = document.createElement("details");
  root.className = "section";
  if (opts.open !== false) root.open = true;
  root.append(summary, content);
  return {
    root,
    content,
    setBadge(text) {
      if (text === null) {
        badge.style.display = "none";
      } else {
        badge.textContent = text;
        badge.style.display = "";
      }
    },
  };
}

export interface NumberOpts {
  label: string;
  value: number;
  min?: number;
  max?: number;
  step?: number;
  unit?: string;
  title?: string;
  onChange: (v: number) => void;
}

function labelEl(text: string, unit?: string, htmlFor?: string): HTMLLabelElement {
  const l = el("label", { text });
  if (unit) l.append(" ", el("span", { class: "unit", text: unit }));
  if (htmlFor) l.htmlFor = htmlFor;
  return l;
}

/**
 * Wrap a numeric <input> with −/+ stepper buttons for fine tuning. Stepping
 * mutates the input value and dispatches real `input`+`change` events so the
 * field's existing handlers (parse, clamp, commit) run unchanged. `step` is in
 * the input's own units (display units for unit-aware fields). Returns the
 * wrapper to place where the bare input used to go.
 */
export function withSteppers(input: HTMLInputElement, step: number): HTMLElement {
  const nudge = (dir: number): void => {
    // Prefer the input's live `step` (unit-aware fields update it on mm/in
    // toggle); fall back to the build-time step when it's "any"/unset.
    const live = parseFloat(input.step);
    const st = Number.isFinite(live) && live > 0 ? live : step;
    const cur = parseLocaleNumber(input.value);
    const base = cur !== null && Number.isFinite(cur) ? cur : 0;
    // Round to the step grid to avoid float drift (0.1+0.2 …), then clamp.
    let next = Math.round((base + dir * st) / st) * st;
    next = Number(next.toFixed(6));
    const min = input.min !== "" ? parseFloat(input.min) : undefined;
    const max = input.max !== "" ? parseFloat(input.max) : undefined;
    if (min !== undefined && next < min) next = min;
    if (max !== undefined && next > max) next = max;
    input.value = String(next);
    input.dispatchEvent(new Event("input", { bubbles: true }));
    input.dispatchEvent(new Event("change", { bubbles: true }));
  };
  const mk = (sym: string, dir: number, aria: string): HTMLButtonElement => {
    const b = el("button", { class: "stepper-btn", text: sym, attrs: { type: "button", tabindex: "-1", "aria-label": aria } }) as HTMLButtonElement;
    b.addEventListener("click", (e) => {
      e.preventDefault();
      nudge(dir);
    });
    return b;
  };
  input.classList.add("has-steppers");
  return el("div", { class: "num-stepper" }, [mk("−", -1, "Decrease"), input, mk("+", 1, "Increase")]);
}

export function numberField(opts: NumberOpts): Field<number> {
  const id = nextId();
  const input = el("input", { attrs: { type: "number", id } }) as HTMLInputElement;
  if (opts.min !== undefined) input.min = String(opts.min);
  if (opts.max !== undefined) input.max = String(opts.max);
  input.step = String(opts.step ?? "any");
  input.value = String(opts.value);
  if (opts.title) input.title = opts.title;
  const commit = (clamp: boolean): void => {
    let v = parseFloat(input.value);
    if (!Number.isFinite(v)) {
      input.classList.add("invalid");
      return;
    }
    input.classList.remove("invalid");
    if (clamp) {
      if (opts.min !== undefined) v = Math.max(opts.min, v);
      if (opts.max !== undefined) v = Math.min(opts.max, v);
      input.value = String(v);
    }
    opts.onChange(v);
  };
  input.addEventListener("input", () => commit(false));
  input.addEventListener("change", () => commit(true));
  const root = el("div", { class: "field" }, [labelEl(opts.label, opts.unit, id), withSteppers(input, opts.step ?? 1)]);
  return {
    root,
    get: () => parseFloat(input.value),
    set: (v) => {
      input.value = String(v);
      input.classList.remove("invalid");
    },
  };
}

export interface SliderOpts extends NumberOpts {
  format?: (v: number) => string;
}

export function sliderField(opts: SliderOpts): Field<number> {
  const id = nextId();
  const readout = el("span", { class: "readout" });
  const fmt = opts.format ?? ((v: number) => String(v));
  const head = el("div", { class: "slider-head" }, [labelEl(opts.label, opts.unit), readout]);
  const range = el("input", { attrs: { type: "range", id } }) as HTMLInputElement;
  range.min = String(opts.min ?? 0);
  range.max = String(opts.max ?? 100);
  range.step = String(opts.step ?? 1);
  range.value = String(opts.value);
  const number = el("input", { attrs: { type: "number" } }) as HTMLInputElement;
  if (opts.min !== undefined) number.min = String(opts.min);
  if (opts.max !== undefined) number.max = String(opts.max);
  number.step = String(opts.step ?? "any");
  number.value = String(opts.value);
  if (opts.title) {
    range.title = opts.title;
    number.title = opts.title;
  }
  const setReadout = (v: number): void => {
    readout.textContent = fmt(v);
  };
  setReadout(opts.value);
  const push = (v: number, clamp: boolean): void => {
    if (!Number.isFinite(v)) return;
    if (clamp) {
      if (opts.min !== undefined) v = Math.max(opts.min, v);
      if (opts.max !== undefined) v = Math.min(opts.max, v);
    }
    range.value = String(v);
    number.value = String(v);
    setReadout(v);
    opts.onChange(v);
  };
  range.addEventListener("input", () => push(parseFloat(range.value), false));
  number.addEventListener("input", () => push(parseFloat(number.value), false));
  number.addEventListener("change", () => push(parseFloat(number.value), true));
  const row = el("div", { class: "slider-row" }, [range, withSteppers(number, opts.step ?? 1)]);
  const root = el("div", { class: "field slider-field" }, [head, row]);
  return {
    root,
    get: () => parseFloat(number.value),
    set: (v) => {
      range.value = String(v);
      number.value = String(v);
      setReadout(v);
    },
  };
}

// ---------------------------------------------------------------------------
// Unit-aware dimension fields. Values are ALWAYS stored + emitted in mm; these
// builders convert at the UI boundary using the active UnitSystem from the
// store, re-render their display + unit label on a unit toggle, and parse
// comma-or-period decimals via parseLocaleNumber.
// ---------------------------------------------------------------------------

export interface DimFieldOpts {
  label: string;
  /** Current value in millimeters. */
  valueMm: number;
  /** Optional bounds, in millimeters. */
  minMm?: number;
  maxMm?: number;
  /** Step in millimeters (converted to display unit for the input). */
  stepMm?: number;
  title?: string;
  onChangeMm: (mm: number) => void;
}

function displayStep(stepMm: number, unit: UnitSystem): string {
  const s = mmToUnit(stepMm, unit);
  return s > 0 ? String(Number(s.toPrecision(4))) : "any";
}

/** A single unit-aware numeric dimension field. Returns its root element. */
export function dimField(ctx: PanelContext, opts: DimFieldOpts): HTMLElement {
  const id = nextId();
  const unit0 = ctx.store.get().unit;
  const input = el("input", { attrs: { type: "text", inputmode: "decimal", id } }) as HTMLInputElement;
  if (opts.title) input.title = opts.title;
  input.value = formatDim(opts.valueMm, unit0);
  const label = labelEl(opts.label, UNIT_LABEL[unit0], id);
  const unitSpan = label.querySelector(".unit") as HTMLElement | null;

  let currentMm = opts.valueMm;
  const commit = (): void => {
    const parsed = parseLocaleNumber(input.value);
    if (parsed === null) {
      input.classList.add("invalid");
      input.setAttribute("aria-invalid", "true");
      return;
    }
    input.classList.remove("invalid");
    input.removeAttribute("aria-invalid");
    let mm = unitToMm(parsed, ctx.store.get().unit);
    if (opts.minMm !== undefined) mm = Math.max(opts.minMm, mm);
    if (opts.maxMm !== undefined) mm = Math.min(opts.maxMm, mm);
    currentMm = mm;
    opts.onChangeMm(mm);
  };
  const reformat = (): void => {
    input.value = formatDim(currentMm, ctx.store.get().unit);
    input.classList.remove("invalid");
    input.removeAttribute("aria-invalid");
  };
  input.addEventListener("input", commit);
  input.addEventListener("change", () => {
    commit();
    reformat();
  });
  input.step = opts.stepMm !== undefined ? displayStep(opts.stepMm, unit0) : "any";

  const stepNum = opts.stepMm !== undefined ? mmToUnit(opts.stepMm, unit0) : 0.01;
  const root = el("div", { class: "field" }, [label, withSteppers(input, stepNum > 0 ? stepNum : 0.01)]);
  // Re-render ONLY when the unit actually changes (the store fires on every
  // edit; reformatting the active input mid-typing would clobber it).
  let lastUnit = unit0;
  ctx.store.subscribe(() => {
    const u = ctx.store.get().unit;
    if (u === lastUnit) return;
    lastUnit = u;
    if (unitSpan) unitSpan.textContent = UNIT_LABEL[u];
    if (opts.stepMm !== undefined) input.step = displayStep(opts.stepMm, u);
    if (document.activeElement !== input) reformat();
  });
  return root;
}

export interface DimSliderOpts extends DimFieldOpts {
  minMm: number;
  maxMm: number;
  stepMm: number;
}

/** A unit-aware slider + numeric readout for a dimension. Returns its root element. */
export function dimSlider(ctx: PanelContext, opts: DimSliderOpts): HTMLElement {
  const id = nextId();
  const unit0 = ctx.store.get().unit;
  const readout = el("span", { class: "readout" });
  const label = labelEl(opts.label, UNIT_LABEL[unit0], id);
  const unitSpan = label.querySelector(".unit") as HTMLElement | null;
  const head = el("div", { class: "slider-head" }, [label, readout]);

  const range = el("input", { attrs: { type: "range", id } }) as HTMLInputElement;
  const number = el("input", { attrs: { type: "text", inputmode: "decimal" } }) as HTMLInputElement;
  if (opts.title) {
    range.title = opts.title;
    number.title = opts.title;
  }

  let currentMm = opts.valueMm;

  const renderUnit = (): void => {
    const u = ctx.store.get().unit;
    range.min = String(mmToUnit(opts.minMm, u));
    range.max = String(mmToUnit(opts.maxMm, u));
    range.step = displayStep(opts.stepMm, u);
    number.step = displayStep(opts.stepMm, u);
    const disp = mmToUnit(currentMm, u);
    range.value = String(disp);
    number.value = formatDim(currentMm, u);
    readout.textContent = formatDim(currentMm, u) + " " + UNIT_LABEL[u];
    if (unitSpan) unitSpan.textContent = UNIT_LABEL[u];
  };
  renderUnit();

  const clampMm = (mm: number): number => {
    let v = mm;
    v = Math.max(opts.minMm, Math.min(opts.maxMm, v));
    return v;
  };

  const pushFromDisplay = (dispRaw: string, live: boolean): void => {
    const parsed = parseLocaleNumber(dispRaw);
    if (parsed === null) {
      number.classList.add("invalid");
      return;
    }
    number.classList.remove("invalid");
    const mm = clampMm(unitToMm(parsed, ctx.store.get().unit));
    currentMm = mm;
    if (!live) renderUnit();
    else {
      const u = ctx.store.get().unit;
      range.value = String(mmToUnit(mm, u));
      readout.textContent = formatDim(mm, u) + " " + UNIT_LABEL[u];
    }
    opts.onChangeMm(mm);
  };

  range.addEventListener("input", () => {
    const parsed = parseLocaleNumber(range.value);
    if (parsed === null) return;
    const mm = clampMm(unitToMm(parsed, ctx.store.get().unit));
    currentMm = mm;
    const u = ctx.store.get().unit;
    number.value = formatDim(mm, u);
    readout.textContent = formatDim(mm, u) + " " + UNIT_LABEL[u];
    opts.onChangeMm(mm);
  });
  number.addEventListener("input", () => pushFromDisplay(number.value, true));
  number.addEventListener("change", () => pushFromDisplay(number.value, false));

  const row = el("div", { class: "slider-row" }, [range, withSteppers(number, mmToUnit(opts.stepMm, unit0) || 0.01)]);
  const root = el("div", { class: "field slider-field" }, [head, row]);
  let lastUnit = unit0;
  ctx.store.subscribe(() => {
    const u = ctx.store.get().unit;
    if (u === lastUnit) return;
    lastUnit = u;
    if (document.activeElement === number || document.activeElement === range) {
      // Only refresh the unit label + slider bounds; leave the value the user
      // is editing alone until they blur.
      if (unitSpan) unitSpan.textContent = UNIT_LABEL[u];
      range.min = String(mmToUnit(opts.minMm, u));
      range.max = String(mmToUnit(opts.maxMm, u));
      range.step = displayStep(opts.stepMm, u);
      return;
    }
    renderUnit();
  });
  return root;
}

export interface SelectOpts<T extends string> {
  label?: string;
  value: T;
  options: { value: T; label: string }[];
  title?: string;
  onChange: (v: T) => void;
}

export function selectField<T extends string>(opts: SelectOpts<T>): Field<T> {
  const id = nextId();
  const sel = el("select", { attrs: { id } }) as HTMLSelectElement;
  for (const o of opts.options) {
    const opt = el("option", { text: o.label }) as HTMLOptionElement;
    opt.value = o.value;
    sel.append(opt);
  }
  sel.value = opts.value;
  if (opts.title) sel.title = opts.title;
  sel.addEventListener("change", () => opts.onChange(sel.value as T));
  const children: (Node | string)[] = [];
  if (opts.label) children.push(labelEl(opts.label, undefined, id));
  children.push(sel);
  const root = el("div", { class: "field" }, children);
  return { root, get: () => sel.value as T, set: (v) => (sel.value = v) };
}

export interface CheckOpts {
  label: string;
  checked: boolean;
  sub?: string;
  title?: string;
  onChange: (v: boolean) => void;
}

export function checkboxField(opts: CheckOpts): Field<boolean> {
  const input = el("input", { attrs: { type: "checkbox" } }) as HTMLInputElement;
  input.checked = opts.checked;
  input.addEventListener("change", () => opts.onChange(input.checked));
  const textNodes: (Node | string)[] = [opts.label];
  if (opts.sub) textNodes.push(el("span", { class: "check-sub", text: opts.sub }));
  const labelText = el("span", {}, textNodes);
  const root = el("label", { class: "check", title: opts.title }, [input, labelText]);
  return { root, get: () => input.checked, set: (v) => (input.checked = v) };
}

export interface RadioOpts<T extends string> {
  label?: string;
  value: T;
  options: { value: T; label: string }[];
  stack?: boolean;
  title?: string;
  onChange: (v: T) => void;
}

export function radioField<T extends string>(opts: RadioOpts<T>): Field<T> {
  const name = nextId();
  const labels: HTMLLabelElement[] = [];
  const group = el("div", { class: "radio-group" + (opts.stack ? " stack" : ""), title: opts.title });
  let current = opts.value;
  for (const o of opts.options) {
    const input = el("input", { attrs: { type: "radio", name } }) as HTMLInputElement;
    input.value = o.value;
    input.checked = o.value === opts.value;
    const label = el("label", { class: input.checked ? "checked" : "" }, [input, o.label]);
    input.addEventListener("change", () => {
      if (!input.checked) return;
      current = o.value;
      for (const l of labels) l.classList.remove("checked");
      label.classList.add("checked");
      opts.onChange(o.value);
    });
    labels.push(label);
    group.append(label);
  }
  const children: (Node | string)[] = [];
  if (opts.label) children.push(el("span", { class: "control-label", text: opts.label }));
  children.push(group);
  const root = el("div", { class: "field" }, children);
  return {
    root,
    get: () => current,
    set: (v) => {
      current = v;
      opts.options.forEach((o, i) => {
        const input = group.children[i].querySelector("input") as HTMLInputElement;
        input.checked = o.value === v;
        labels[i].classList.toggle("checked", o.value === v);
      });
    },
  };
}

export interface TextOpts {
  label: string;
  value: string;
  placeholder?: string;
  title?: string;
  onChange: (v: string) => void;
}

export function textField(opts: TextOpts): Field<string> {
  const id = nextId();
  const input = el("input", { attrs: { type: "text", id } }) as HTMLInputElement;
  input.value = opts.value;
  if (opts.placeholder) input.placeholder = opts.placeholder;
  if (opts.title) input.title = opts.title;
  input.addEventListener("input", () => opts.onChange(input.value));
  const root = el("div", { class: "field" }, [labelEl(opts.label, undefined, id), input]);
  return { root, get: () => input.value, set: (v) => (input.value = v) };
}

export function button(label: string, opts: { variant?: string; title?: string; onClick: () => void } = { onClick: () => {} }): HTMLButtonElement {
  const b = el("button", { class: "btn" + (opts.variant ? " " + opts.variant : ""), text: label }) as HTMLButtonElement;
  if (opts.title) b.title = opts.title;
  b.addEventListener("click", opts.onClick);
  return b;
}

export function swatch(color: string, large = false): HTMLElement {
  const s = el("span", { class: "swatch" + (large ? " lg" : "") });
  s.style.background = color;
  return s;
}

/** Full MOPA cut-parameter editor (Q-pulse is a discrete standard-step dropdown). */
export function cutParamsEditor(initial: CutParams, onChange: (p: CutParams) => void): HTMLElement {
  const value: CutParams = { ...initial };
  const emit = (): void => onChange({ ...value });
  const grid = el("div", { class: "cutparams" });
  grid.append(
    numberField({ label: "Power", unit: "%", value: value.power, min: 0, max: 100, step: 0.1, onChange: (v) => { value.power = v; emit(); } }).root,
    numberField({ label: "Speed", unit: "mm/s", value: value.speed, min: 1, step: 10, onChange: (v) => { value.speed = v; emit(); } }).root,
    numberField({ label: "Frequency", unit: "kHz", value: value.frequencyKhz, min: 1, max: 4000, step: 1, onChange: (v) => { value.frequencyKhz = v; emit(); } }).root,
    selectField({
      label: "Pulse width (Q-pulse, ns)",
      title: "Pulse width — the Q-pulse setting, in nanoseconds. Discrete standard MOPA steps.",
      value: String(nearestQPulseStep(value.qPulseNs)),
      options: Q_PULSE_STEPS.map((n) => ({ value: String(n), label: String(n) })),
      onChange: (v) => { value.qPulseNs = Number(v); emit(); },
    }).root,
    numberField({ label: "Line interval", unit: "mm", title: "Hatch line spacing for fills, in millimeters.", value: value.intervalMm, min: 0.001, step: 0.001, onChange: (v) => { value.intervalMm = v; emit(); } }).root,
    numberField({ label: "Passes", value: value.passes, min: 1, step: 1, onChange: (v) => { value.passes = Math.round(v); emit(); } }).root,
  );

  // Scan angle + its live direction swatch share one tidy two-column row.
  const preview = el("div", { class: "scan-preview-svg" });
  const caption = el("div", { class: "scan-preview-cap" });
  const redrawPreview = (): void => {
    preview.innerHTML = scanAnglePreviewSVG(value.hatchAngleDeg, value.bidir);
    caption.textContent = value.bidir ? "Bi-directional" : "Uni-directional";
  };
  const angleField = numberField({ label: "Scan angle", unit: "°", title: "Fill scan / hatch angle in degrees.", value: value.hatchAngleDeg, min: 0, max: 360, step: 5, onChange: (v) => { value.hatchAngleDeg = ((v % 360) + 360) % 360; redrawPreview(); emit(); } });
  const scanRow = el("div", { class: "scan-row" }, [
    angleField.root,
    el("div", { class: "scan-preview" }, [preview, caption]),
  ]);

  const toggles = el("div", { class: "cutparams-toggles" });
  toggles.append(
    checkboxField({ label: "Cross-hatch", title: "Add a second fill pass at 90° to the scan angle.", checked: value.crossHatch, onChange: (v) => { value.crossHatch = v; emit(); } }).root,
    checkboxField({ label: "Bi-directional", title: "Fill on both sweep directions (faster). Off = one direction only.", checked: value.bidir, onChange: (v) => { value.bidir = v; redrawPreview(); emit(); } }).root,
    checkboxField({ label: "Fill all shapes at once", title: "On: fill every shape in one pass. Off: fill each shape individually.", checked: value.fillAllAtOnce, onChange: (v) => { value.fillAllAtOnce = v; emit(); } }).root,
  );
  redrawPreview();
  const wrap = el("div", { class: "cutparams-wrap" }, [grid, scanRow, toggles]);
  return wrap;
}

// ===========================================================================
// Panel context + gating helpers.
// ===========================================================================

export interface PanelActions {
  persist(): void;
  scheduleVectorPreview(): void;
  schedulePhotoPreview(): void;
  generateVector(): void;
  cancel(): void;
  browseVector(): void;
  browsePhotoImage(): void;
  browsePhotoFillImage(): void;
  exportVectorSvg(): void;
  exportVectorLbrn(): void;
  exportVectorPaletteCsv(): void;
  exportVectorReport(): void;
  exportPhotoSvg(): void;
  exportPhotoCsv(): void;
  exportPhotoReport(): void;
  exportPhotoLbrn(): void;
  exportTestGrid(): void;
  toast(kind: ToastKind, msg: string): void;
}

export interface PanelContext {
  store: Store;
  actions: PanelActions;
}

export interface PanelHandle {
  element: HTMLElement;
}

export function currentVectorSignature(s: AppState): string {
  return vectorSignature({
    fileName: s.docFileName,
    layers: s.selectedLayers,
    pixel: s.pixel,
    photoFill: s.photoFill,
    photoFillImageName: s.photoFillImage ? s.photoFillImage.name : null,
  });
}

export function currentPhotoSignature(s: AppState): string {
  return photoSignature(s.photoImage ? s.photoImage.name : null, s.photo);
}

export function vectorExportReady(s: AppState): boolean {
  if (!s.doc || s.selectedLayers.size === 0 || !s.vectorResult || s.vectorTruncated) return false;
  return exportReady(currentVectorSignature(s), s.vectorFullSignature);
}

export function photoExportReady(s: AppState): boolean {
  if (!s.photoResult) return false;
  return s.photoResultSignature === currentPhotoSignature(s);
}

// ===========================================================================
// Controller.
// ===========================================================================

const VECTOR_ACCEPT = ".lbrn,.lbrn2,.svg";
const IMAGE_ACCEPT = "image/*,.svg";
const DEBOUNCE_MS = 250;

class AppController {
  readonly store: Store;
  private canvasView: CanvasView;
  private progress: ProgressOverlay;
  private pixelate = new PixelateClient();
  private photoClient = new PhotoClient();

  private dropOverlay!: HTMLElement;
  private emptyState!: HTMLElement;
  private emptyTitle!: HTMLElement;
  private emptyText!: HTMLElement;
  private emptyActions!: HTMLElement;
  private topbarFile!: HTMLElement;
  private zoomReadout!: HTMLElement;
  private statusBar!: HTMLElement;
  private panelEl!: HTMLElement;
  private versionChip!: HTMLButtonElement;
  private unitToggle!: HTMLElement;
  private panelToggleBtn!: HTMLButtonElement;
  private firstRunHint: HTMLElement | null = null;
  private compareBtn: HTMLButtonElement | null = null;

  // Responsive layout state. `layoutTier` is the currently-applied tier; the
  // ancillary chrome (bottom-sheet handle, thumb-zone action bar, icon rail) is
  // created lazily the first time a phone tier is entered.
  private layoutTier: "desktop" | "dock" | "sheet" | "drawer" = "desktop";
  private sheetHandle: HTMLElement | null = null;
  private actionBar: HTMLElement | null = null;
  private toolRail: HTMLElement | null = null;
  private layoutMedia: MediaQueryList[] = [];
  private workspaceEl!: HTMLElement;
  private exportAllBtn: HTMLButtonElement | null = null;
  private compareActive = false;
  private compareSourceKey = "";
  private compareSourceCanvas: HTMLCanvasElement | null = null;

  private vectorGeoms: PolyGeom[] = [];
  private vectorGeomsKey = "";
  private previewTimer: number | null = null;
  private photoTimer: number | null = null;
  private shownWarnings = new Set<string>();
  private dragCounter = 0;
  private lastCanvasKey = "";
  private root: HTMLElement;

  constructor(root: HTMLElement) {
    this.root = root;
    this.store = new Store(createInitialState());

    const overlayHost = el("div", { class: "overlay-host" });
    const toastHost = el("div", { class: "toast-host" });
    document.body.append(overlayHost, toastHost);
    initToasts(toastHost);
    this.progress = new ProgressOverlay(overlayHost, () => this.cancel());

    this.buildShell();
    const canvasHost = this.root.querySelector(".canvas-host") as HTMLElement;
    this.canvasView = new CanvasView(canvasHost, (v) => {
      this.zoomReadout.textContent = Math.round(v.zoom * 100) + "%";
    });

    this.store.subscribe(() => this.onStateChange());
    this.wireGlobalEvents();
    this.setupResponsiveLayout();
    this.onStateChange();
    this.maybeShowFirstRun();
  }

  // ------------------------------------------------------- first-run hint
  private maybeShowFirstRun(): void {
    if (this.store.get().firstRunDone) return;
    const hint = el("div", { class: "onboarding", attrs: { role: "note" } });
    // Anchor near the bottom-left of the (position:relative) canvas region so it
    // floats above the canvas host without covering the whole hero.
    hint.style.left = "16px";
    hint.style.bottom = "16px";
    const dismiss = button("Got it", { variant: "primary sm", onClick: () => this.dismissFirstRun() });
    hint.append(
      el("div", { class: "onboarding-body" }, [
        el("strong", { text: "Welcome!" }),
        el("span", {
          text:
            " Drop a LightBurn/SVG file or a photo — or tap “Try a sample”. Everything runs in your browser; nothing is uploaded.",
        }),
      ]),
      dismiss,
    );
    this.firstRunHint = hint;
    // Anchor near the canvas region so it's visible above the fold.
    const region = this.root.querySelector(".canvas-region");
    if (region) region.append(hint);
    else document.body.append(hint);
    dismiss.focus();
  }

  private dismissFirstRun(): void {
    if (this.firstRunHint) {
      this.firstRunHint.remove();
      this.firstRunHint = null;
    }
    if (!this.store.get().firstRunDone) {
      this.store.set({ firstRunDone: true });
      savePersisted(this.store.get());
    }
  }

  // ------------------------------------------------------------------ shell
  private buildShell(): void {
    // Version chip is an accessible button opening the about/changelog modal.
    this.versionChip = el("button", {
      class: "version-chip",
      text: "v" + APP_VERSION,
      title: "About " + APP_NAME + " — version, changelog, privacy",
      attrs: { type: "button", "aria-haspopup": "dialog" },
    }) as HTMLButtonElement;
    this.versionChip.addEventListener("click", () => openAboutModal(this.versionChip));
    const brand = el("div", { class: "brand" }, [
      el("img", { attrs: { src: FAVICON_URL, alt: "" } }),
      el("span", { class: "brand-name", text: APP_NAME }),
      this.versionChip,
    ]);
    this.topbarFile = el("div", { class: "topbar-file", html: "No file loaded" });

    // Units toggle (mm / inch) — applies to every dimension field + readout.
    this.unitToggle = this.buildUnitToggle();

    this.zoomReadout = el("div", { class: "zoom-readout", text: "100%", title: "Click to fit (Ctrl+0)" });
    this.zoomReadout.addEventListener("click", () => this.canvasView.fit());

    const compareBtn = button("Compare", {
      variant: "ghost sm compare-btn",
      title: "Toggle before/after compare (press C)",
      onClick: () => this.toggleCompare(),
    });
    compareBtn.setAttribute("aria-pressed", "false");
    compareBtn.disabled = true;
    this.compareBtn = compareBtn;

    const exportAllBtn = button("Export all", {
      variant: "sm export-all-btn",
      title: "Save the SVG, palette CSV, LightBurn file, and report as one .zip",
      onClick: () => void this.exportAll(),
    });
    this.exportAllBtn = exportAllBtn;

    const helpBtn = button("?", {
      variant: "ghost sm help-btn",
      title: "Help & keyboard shortcuts (press ?)",
      onClick: () => openHelpModal(helpBtn),
    });
    helpBtn.setAttribute("aria-haspopup", "dialog");
    helpBtn.setAttribute("aria-label", "Help and keyboard shortcuts");

    const trust = el("div", { class: "trust-line" }, [
      el("span", {
        html: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 1 3 5v6c0 5 3.8 9.7 9 11 5.2-1.3 9-6 9-11V5l-9-4Zm-1 15-4-4 1.4-1.4L11 13.2l5.6-5.6L18 9l-7 7Z"/></svg>',
      }),
      "Your files are processed in your browser and never uploaded. No accounts, no tracking.",
    ]);
    const panelToggle = button("Settings", {
      variant: "ghost sm panel-toggle",
      onClick: () => this.togglePanel(),
    });
    panelToggle.classList.add("panel-toggle");
    panelToggle.setAttribute("aria-expanded", "false");
    panelToggle.setAttribute("aria-controls", "control-panel");
    this.panelToggleBtn = panelToggle;
    const topbar = el("header", { class: "topbar" }, [
      brand,
      this.topbarFile,
      el("div", { class: "topbar-spacer" }),
      // Mount point for T9's deferred install affordance (populated if offered).
      el("div", { class: "install-slot", attrs: { id: "install-slot" } }),
      this.unitToggle,
      this.zoomReadout,
      compareBtn,
      exportAllBtn,
      helpBtn,
      trust,
      panelToggle,
    ]);

    // canvas region + empty state + drop overlay
    const canvasHost = el("div", { class: "canvas-host" });
    this.emptyTitle = el("h2", { text: "Drop a LightBurn file or photo" });
    this.emptyText = el("p", {
      text: "Import .lbrn / .lbrn2 / .svg to pixelate vectors, or an image to build color layers.",
    });
    this.emptyActions = el("div", { class: "empty-actions" });
    const dropzone = el(
      "div",
      { class: "dropzone", attrs: { tabindex: "0", role: "button", "aria-label": "Drop a file or click to browse" } },
      [
        el("img", { class: "glyph", attrs: { src: FAVICON_URL, alt: "" } }),
        this.emptyTitle,
        this.emptyText,
        this.emptyActions,
        el("div", { class: "formats", text: ".lbrn  .lbrn2  .svg  .png  .jpg  .webp  .gif  .bmp" }),
        el("p", { class: "formats", text: "drag & drop anywhere · click to browse · Ctrl+V to paste" }),
      ],
    );
    dropzone.addEventListener("click", (e) => {
      // Clicks on the sample/preset buttons handle themselves.
      if ((e.target as HTMLElement).closest(".empty-actions")) return;
      this.browseForActiveTab();
    });
    dropzone.addEventListener("keydown", (e) => {
      if (e.target !== dropzone) return;
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        this.browseForActiveTab();
      }
    });
    this.emptyState = el("div", { class: "empty-state" }, [dropzone]);
    const canvasRegion = el("div", { class: "canvas-region" }, [canvasHost, this.emptyState]);

    // right panel with tabs
    this.panelEl = el("aside", { class: "panel", attrs: { id: "control-panel" } });
    const tabStrip = el("div", { class: "tabs", attrs: { role: "tablist", "aria-label": "Workflow" } });
    const bodies = el("div", { class: "tab-body" });
    const ctx: PanelContext = { store: this.store, actions: this.actions() };
    const panels: { id: TabId; label: string; handle: PanelHandle }[] = [
      { id: "vector", label: "Vector", handle: createVectorPanel(ctx) },
      { id: "photo", label: "Photo", handle: createPhotoPanel(ctx) },
      { id: "tools", label: "Calibration", handle: createToolsPanel(ctx) },
    ];
    const order: TabId[] = panels.map((p) => p.id);
    const tabButtons = new Map<TabId, HTMLButtonElement>();
    for (const p of panels) {
      const tabId = "tab-" + p.id;
      const panelId = "tabpanel-" + p.id;
      const tabBtn = el("button", {
        class: "tab",
        text: p.label,
        attrs: { role: "tab", id: tabId, "aria-controls": panelId, "aria-selected": "false", tabindex: "-1" },
      }) as HTMLButtonElement;
      tabBtn.addEventListener("click", () => this.setTab(p.id));
      tabBtn.addEventListener("keydown", (e) => this.onTabKeydown(e, p.id, order));
      tabButtons.set(p.id, tabBtn);
      tabStrip.append(tabBtn);
      p.handle.element.classList.add("tab-panel");
      p.handle.element.dataset.tab = p.id;
      p.handle.element.setAttribute("role", "tabpanel");
      p.handle.element.id = panelId;
      p.handle.element.setAttribute("aria-labelledby", tabId);
      p.handle.element.tabIndex = 0;
      bodies.append(p.handle.element);
    }
    this.tabButtons = tabButtons;
    this.panelBodies = bodies;
    this.panelEl.append(tabStrip, bodies);

    const workspace = el("div", { class: "workspace" }, [canvasRegion, this.panelEl]);
    this.workspaceEl = workspace;
    this.statusBar = el("div", { class: "status-bar" });
    this.dropOverlay = el("div", { class: "drop-overlay" }, [
      el("div", { class: "ring", text: "Drop to load" }),
    ]);

    const shell = el("div", { class: "app-shell" }, [topbar, workspace, this.statusBar]);
    clearNode(this.root);
    this.root.append(shell);
    document.body.append(this.dropOverlay);
  }

  private tabButtons!: Map<TabId, HTMLButtonElement>;
  private panelBodies!: HTMLElement;

  /** Segmented mm / inch toggle. Changing it re-renders every dimension field. */
  private buildUnitToggle(): HTMLElement {
    const group = el("div", { class: "unit-toggle", attrs: { role: "group", "aria-label": "Measurement units" } });
    const opts: { value: UnitSystem; label: string }[] = [
      { value: "mm", label: "mm" },
      { value: "inch", label: "in" },
    ];
    const btns = new Map<UnitSystem, HTMLButtonElement>();
    const sync = (): void => {
      const active = this.store.get().unit;
      for (const [u, b] of btns) {
        b.classList.toggle("active", u === active);
        b.setAttribute("aria-pressed", String(u === active));
      }
    };
    for (const o of opts) {
      const b = el("button", {
        class: "unit-btn",
        text: o.label,
        attrs: { type: "button", "aria-pressed": "false", title: o.value === "mm" ? "Millimeters" : "Inches" },
      }) as HTMLButtonElement;
      b.addEventListener("click", () => {
        if (this.store.get().unit === o.value) return;
        this.store.set({ unit: o.value });
        savePersisted(this.store.get());
        sync();
      });
      btns.set(o.value, b);
      group.append(b);
    }
    sync();
    return group;
  }

  private actions(): PanelActions {
    return {
      persist: () => savePersisted(this.store.get()),
      scheduleVectorPreview: () => this.scheduleVectorPreview(),
      schedulePhotoPreview: () => this.schedulePhotoPreview(),
      generateVector: () => this.generateVector(),
      cancel: () => this.cancel(),
      browseVector: () => this.browse(VECTOR_ACCEPT, (f) => this.handleFiles(f)),
      browsePhotoImage: () => this.browse(IMAGE_ACCEPT, (f) => this.loadPhotoImage(f[0])),
      browsePhotoFillImage: () => this.browse(IMAGE_ACCEPT, (f) => this.loadPhotoFillImage(f[0])),
      exportVectorSvg: () => this.exportVectorSvg(),
      exportVectorLbrn: () => this.exportVectorLbrn(),
      exportVectorPaletteCsv: () => this.exportVectorPaletteCsv(),
      exportVectorReport: () => this.exportVectorReport(),
      exportPhotoSvg: () => this.exportPhotoSvg(),
      exportPhotoCsv: () => this.exportPhotoCsv(),
      exportPhotoReport: () => this.exportPhotoReport(),
      exportPhotoLbrn: () => this.exportPhotoLbrn(),
      exportTestGrid: () => this.exportTestGrid(),
      toast: (kind, msg) => toast(kind, msg),
    };
  }

  // -------------------------------------------------------------- reactive
  private onStateChange(): void {
    const s = this.store.get();
    // tabs — active class + ARIA selected state + roving tabindex.
    for (const [id, btn] of this.tabButtons) {
      const active = id === s.activeTab;
      btn.classList.toggle("active", active);
      btn.setAttribute("aria-selected", String(active));
      btn.tabIndex = active ? 0 : -1;
    }
    for (const child of Array.from(this.panelBodies.children)) {
      (child as HTMLElement).classList.toggle("active", (child as HTMLElement).dataset.tab === s.activeTab);
    }
    // header
    this.topbarFile.textContent = s.docFileName ?? (s.photoImage ? s.photoImage.name : "No file loaded");
    this.statusBar.textContent = s.status;
    this.refreshEmptyState(s);
    this.refreshCanvas(s);
    this.refreshCompareAvailability(s);
  }

  private onTabKeydown(e: KeyboardEvent, id: TabId, order: TabId[]): void {
    const idx = order.indexOf(id);
    let next = -1;
    if (e.key === "ArrowRight" || e.key === "ArrowDown") next = (idx + 1) % order.length;
    else if (e.key === "ArrowLeft" || e.key === "ArrowUp") next = (idx - 1 + order.length) % order.length;
    else if (e.key === "Home") next = 0;
    else if (e.key === "End") next = order.length - 1;
    else return;
    e.preventDefault();
    const target = order[next];
    this.setTab(target);
    this.tabButtons.get(target)?.focus();
  }

  private isPanelOpen(): boolean {
    if (this.layoutTier === "desktop") return true; // always-visible docked panel
    if (this.layoutTier === "dock") return !this.panelEl.classList.contains("collapsed");
    if (this.layoutTier === "sheet") return true; // always-visible peek/half/full
    return this.panelEl.classList.contains("open"); // drawer
  }

  private togglePanel(): void {
    this.setPanelOpen(!this.isPanelOpen());
  }

  /**
   * Open/close the control panel and keep focus + inertness correct.
   *
   * On tiers where the panel is off-canvas (phone-portrait sheet, phone-
   * landscape drawer) a closed panel must NOT be reachable by Tab — otherwise
   * focus escapes into an invisible off-screen panel (a P0 focus trap). We set
   * `inert` + `aria-hidden` while closed and clear them on open, moving focus
   * into the panel on open and restoring it to the toggle on close.
   */
  private setPanelOpen(open: boolean): void {
    const wasOpen = this.isPanelOpen();
    if (this.layoutTier === "dock") {
      // The dock is in-flow; "open" == not collapsed.
      this.panelEl.classList.toggle("collapsed", !open);
    } else {
      this.panelEl.classList.toggle("open", open);
    }
    this.panelToggleBtn.setAttribute("aria-expanded", String(open));
    this.applyPanelInertness();
    if (open && !wasOpen) {
      // Move focus into the panel (first focusable, else the panel itself).
      const first = this.panelEl.querySelector<HTMLElement>(
        'button:not([disabled]),[href],input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])',
      );
      (first ?? this.panelEl).focus();
    } else if (!open && wasOpen) {
      this.panelToggleBtn.focus();
    }
  }

  /**
   * Reflect the current tier + open state onto the panel's `inert`/`aria-hidden`
   * attributes. The panel is off-canvas (and must be inert when closed) only on
   * the phone tiers; on tablet/desktop it is always in-flow and interactive.
   */
  private applyPanelInertness(): void {
    // The bottom sheet (phone portrait) is always on-screen as a peek, so it is
    // never inert. Only the landscape slide-over drawer is translated fully
    // off-canvas when closed — that is the focus trap to guard against.
    const offCanvas = this.layoutTier === "drawer";
    const inert = offCanvas && !this.panelEl.classList.contains("open");
    // `inert` is a reflected boolean property in supporting engines; set the
    // attribute too for older ones and to keep the CSS `[inert]`/`[aria-hidden]`
    // guards in sync.
    if (inert) {
      this.panelEl.setAttribute("inert", "");
      this.panelEl.setAttribute("aria-hidden", "true");
    } else {
      this.panelEl.removeAttribute("inert");
      this.panelEl.removeAttribute("aria-hidden");
    }
  }

  // --------------------------------------------------------- responsive layout
  /**
   * Drive the 4-tier responsive layout the stylesheet was written for. The CSS
   * repositions the panel per tier, but the tier-specific classes/elements
   * (`.sheet`+`data-detent`, `.dock`+`.collapsed`, `.drawer`, `.sheet-handle`,
   * `.action-bar`, `.tool-rail`) only take effect once JS applies them — so this
   * controller listens on matchMedia + orientation and reshapes the DOM to match
   * the active breakpoint. Without it the phone/tablet CSS never engages and the
   * controls are unreachable behind the canvas.
   */
  private setupResponsiveLayout(): void {
    const queries = [
      window.matchMedia("(max-width: 1079.98px)"),
      window.matchMedia("(max-width: 767.98px)"),
      window.matchMedia("(orientation: landscape)"),
    ];
    for (const q of queries) q.addEventListener("change", this.onLayoutMediaChange);
    this.layoutMedia = queries;
    this.applyLayoutTier();
  }

  private onLayoutMediaChange = (): void => {
    this.applyLayoutTier();
  };

  private computeLayoutTier(): "desktop" | "dock" | "sheet" | "drawer" {
    const w = window.innerWidth;
    if (w >= 1080) return "desktop";
    if (w >= 768) return "dock";
    // < 768: phone/small-tablet. Landscape gets the rail + slide-over drawer so a
    // short canvas isn't starved; portrait gets the bottom sheet.
    return window.matchMedia("(orientation: landscape)").matches ? "drawer" : "sheet";
  }

  private applyLayoutTier(): void {
    const tier = this.computeLayoutTier();
    this.layoutTier = tier;

    // Reset the mutually-exclusive tier classes + stale open/collapsed state so
    // switching tiers never leaves a class from the previous tier behind.
    const p = this.panelEl;
    p.classList.remove("sheet", "dock", "drawer", "open", "collapsed");
    // The bottom-sheet detent (peek/half/full) is only meaningful in the sheet
    // tier. Leaving it on the element after switching to dock/desktop leaks a
    // stale height hint into other tiers; clear it whenever we're not a sheet.
    if (tier !== "sheet") delete p.dataset.detent;

    // Ancillary chrome visibility (created lazily, then shown/hidden per tier).
    this.ensureLayoutChrome(tier);
    const showSheetChrome = tier === "sheet";
    const showRail = tier === "drawer";
    if (this.sheetHandle) this.sheetHandle.hidden = !showSheetChrome;
    if (this.actionBar) this.actionBar.hidden = !showSheetChrome;
    if (this.toolRail) this.toolRail.hidden = !showRail;

    switch (tier) {
      case "sheet":
        // Always-visible bottom sheet; starts at the peek detent.
        p.classList.add("sheet");
        p.dataset.detent = p.dataset.detent ?? "peek";
        break;
      case "drawer":
        // Off-canvas slide-over; closed by default (opened via the rail/toggle).
        p.classList.add("drawer");
        break;
      case "dock":
        // In-flow collapsible dock; starts expanded.
        p.classList.add("dock");
        break;
      case "desktop":
        // Base layout; panel is docked and always visible (no extra class).
        break;
    }
    this.panelToggleBtn.setAttribute("aria-expanded", String(this.isPanelOpen()));
    this.applyPanelInertness();
  }

  /**
   * Expand the bottom sheet toward its full detent (peek→half→full) and move
   * focus into it, so the phone "Settings" action opens the controls.
   */
  private expandSheet(): void {
    if (this.layoutTier !== "sheet") return;
    const cur = this.panelEl.dataset.detent ?? "peek";
    this.panelEl.dataset.detent = cur === "peek" ? "half" : "full";
    const first = this.panelEl.querySelector<HTMLElement>(
      'button:not([disabled]),input:not([disabled]),select:not([disabled]),[tabindex]:not([tabindex="-1"])',
    );
    (first ?? this.panelEl).focus();
  }

  /** Create the bottom-sheet handle, thumb-zone action bar, and icon rail once. */
  private ensureLayoutChrome(tier: "desktop" | "dock" | "sheet" | "drawer"): void {
    if ((tier === "sheet") && !this.sheetHandle) {
      const handle = el("div", {
        class: "sheet-handle",
        attrs: { role: "button", tabindex: "0", "aria-label": "Resize controls" },
      });
      const cycle = (): void => {
        const order = ["peek", "half", "full"] as const;
        const cur = (this.panelEl.dataset.detent ?? "peek") as (typeof order)[number];
        const next = order[(order.indexOf(cur) + 1) % order.length];
        this.panelEl.dataset.detent = next;
      };
      handle.addEventListener("click", cycle);
      handle.addEventListener("keydown", (e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          cycle();
        }
      });
      this.sheetHandle = handle;
      // The handle lives at the very top of the sheet, above the tab strip.
      this.panelEl.prepend(handle);
    }
    if ((tier === "sheet") && !this.actionBar) {
      const bar = el("div", { class: "action-bar", attrs: { role: "toolbar", "aria-label": "Primary actions" } });
      bar.append(
        button("Import", { variant: "ghost", onClick: () => this.browseForActiveTab() }),
        button("Compare", { variant: "ghost", onClick: () => this.toggleCompare() }),
        button("Export", { variant: "ghost", onClick: () => void this.exportAll() }),
        button("Settings", { variant: "ghost", onClick: () => this.expandSheet() }),
      );
      this.actionBar = bar;
      document.body.append(bar);
    }
    if (tier === "drawer" && !this.toolRail) {
      const rail = el("div", { class: "tool-rail", attrs: { role: "toolbar", "aria-label": "Tools" } });
      rail.append(
        button("Import", { variant: "ghost", title: "Import a file", onClick: () => this.browseForActiveTab() }),
        button("Compare", { variant: "ghost", title: "Toggle before/after compare", onClick: () => this.toggleCompare() }),
        button("Export", { variant: "ghost", title: "Export all", onClick: () => void this.exportAll() }),
        button("Settings", { variant: "ghost", title: "Show controls", onClick: () => this.togglePanel() }),
      );
      this.toolRail = rail;
      // The rail sits at the left edge of the workspace, before the canvas.
      this.workspaceEl.prepend(rail);
    }
  }

  private refreshEmptyState(s: AppState): void {
    let show = false;
    if (s.activeTab === "vector") show = !s.doc;
    else if (s.activeTab === "photo") show = !s.photoImage;
    else show = false;
    if (s.activeTab === "tools") {
      this.emptyState.classList.add("hidden");
      return;
    }
    this.emptyState.classList.toggle("hidden", !show);
    if (!show) return;
    clearNode(this.emptyActions);
    if (s.activeTab === "vector") {
      this.emptyTitle.textContent = "Drop a LightBurn or SVG file";
      this.emptyText.textContent = "Import .lbrn / .lbrn2 / .svg to turn vector shapes into laser dot grids.";
      this.emptyActions.append(
        button("Browse files", { variant: "primary", onClick: () => this.browse(VECTOR_ACCEPT, (f) => this.handleFiles(f)) }),
        button("Try a sample", { variant: "ghost", title: "Load a bundled example — no upload, runs locally.", onClick: () => void this.loadSample("vector") }),
      );
    } else {
      this.emptyTitle.textContent = "Drop a photo to color-separate";
      this.emptyText.textContent = "Import an image to reduce it to up to 30 MOPA color layers.";
      this.emptyActions.append(
        button("Browse images", { variant: "primary", onClick: () => this.browse(IMAGE_ACCEPT, (f) => this.loadPhotoImage(f[0])) }),
        button("Try a sample", { variant: "ghost", title: "Load a bundled example — no upload, runs locally.", onClick: () => void this.loadSample("photo") }),
      );
    }
  }

  /**
   * Load a bundled sample asset. These live under public/samples/ and are served
   * same-origin as static app files — fetching them is NOT a network/analytics
   * call, it is reading a file that shipped with the app.
   */
  private async loadSample(kind: "vector" | "photo"): Promise<void> {
    const base = import.meta.env.BASE_URL;
    const path = kind === "vector" ? "samples/sample.lbrn2" : "samples/sample.png";
    try {
      const res = await fetch(base + path);
      if (!res.ok) throw new Error("sample not found (" + res.status + ")");
      const blob = await res.blob();
      const fileName = kind === "vector" ? "sample.lbrn2" : "sample.png";
      const file = new File([blob], fileName, { type: blob.type });
      this.dismissFirstRun();
      if (kind === "vector") await this.importVectorFile(file);
      else await this.loadPhotoImage(file);
    } catch {
      toast(
        "warn",
        "The bundled sample could not be loaded. Drop your own " +
          (kind === "vector" ? "LightBurn or SVG file" : "image") +
          " to get started.",
      );
    }
  }

  private refreshCanvas(s: AppState): void {
    const usingFillNow = s.photoFill.enabled && !!s.vectorFillResult;
    const key = [
      s.activeTab,
      idOf(s.vectorResult),
      idOf(s.vectorFillResult),
      idOf(s.photoResult),
      usingFillNow ? 1 : 0,
      s.pixel.shape,
      s.pixel.pixelSize,
    ].join("|");
    if (key === this.lastCanvasKey) return;
    this.lastCanvasKey = key;
    if (s.activeTab === "vector" && s.vectorResult) {
      const usingFill = s.photoFill.enabled && !!s.vectorFillResult;
      const pixels = usingFill ? s.vectorFillResult!.pixels : s.vectorResult.pixels;
      const layerColors = usingFill
        ? photoPaletteLayerColors(s.vectorFillResult!.palette)
        : this.docLayerColors(s.doc);
      this.canvasView.setContent({
        geoms: this.vectorGeoms,
        pixels,
        layerColors,
        pixelSizeMm: s.pixel.pixelSize,
        shape: s.pixel.shape,
        aspect: s.pixel.aspect,
      });
    } else if (s.activeTab === "photo" && s.photoResult) {
      this.canvasView.setContent({
        geoms: [],
        pixels: photoResultToPixels(s.photoResult),
        layerColors: photoPaletteLayerColors(s.photoResult.palette),
        pixelSizeMm: s.photoResult.pixelSize,
        shape: s.photoResult.shape,
      });
    } else {
      this.canvasView.setContent(null);
    }
  }

  private docLayerColors(doc: VectorDocument | null): Record<number, string> {
    const out: Record<number, string> = {};
    if (doc) for (const li of doc.layers) out[li.index] = li.color;
    return out;
  }

  // -------------------------------------------------------------- compare
  /** The raster "original" to compare against, if any (photo image / fill image). */
  private compareSourceImage(s: AppState): RawImage | null {
    if (s.activeTab === "photo") return s.photoImage;
    if (s.activeTab === "vector" && s.photoFill.enabled) return s.photoFillImage;
    return null;
  }

  private rawImageToCanvas(img: RawImage): HTMLCanvasElement {
    const c = document.createElement("canvas");
    c.width = img.width;
    c.height = img.height;
    const cx = c.getContext("2d");
    if (cx) {
      const imageData = cx.createImageData(img.width, img.height);
      imageData.data.set(img.data);
      cx.putImageData(imageData, 0, 0);
    }
    return c;
  }

  private refreshCompareAvailability(s: AppState): void {
    if (!this.compareBtn) return;
    const src = this.compareSourceImage(s);
    const hasResult = s.activeTab === "photo" ? !!s.photoResult : !!s.vectorResult;
    const available = !!src && hasResult;
    this.compareBtn.disabled = !available;
    if (!available && this.compareActive) this.setCompare(false);
    // Refresh the source only when it changes.
    const key = src ? src.name + ":" + src.width + "x" + src.height + ":" + s.activeTab : "";
    if (key !== this.compareSourceKey) {
      this.compareSourceKey = key;
      this.compareSourceCanvas = src ? this.rawImageToCanvas(src) : null;
      this.canvasView.setCompareSource(this.compareSourceCanvas);
    }
    // Keep the export-all button gated on an exportable result.
    if (this.exportAllBtn) {
      const ready = s.activeTab === "photo" ? photoExportReady(s) : vectorExportReady(s);
      this.exportAllBtn.disabled = !ready;
      this.exportAllBtn.style.display = s.activeTab === "tools" ? "none" : "";
    }
  }

  private toggleCompare(): void {
    if (this.compareBtn?.disabled) return;
    this.setCompare(!this.compareActive);
  }

  private setCompare(on: boolean): void {
    this.compareActive = on;
    this.canvasView.setCompareMode(on);
    if (this.compareBtn) {
      this.compareBtn.classList.toggle("active", on);
      this.compareBtn.setAttribute("aria-pressed", String(on));
    }
  }

  private setTab(tab: TabId): void {
    this.store.set({ activeTab: tab });
    savePersisted(this.store.get());
  }

  private browseForActiveTab(): void {
    const tab = this.store.get().activeTab;
    if (tab === "photo") this.browse(IMAGE_ACCEPT, (f) => this.loadPhotoImage(f[0]));
    else this.browse(VECTOR_ACCEPT, (f) => this.handleFiles(f));
  }

  // --------------------------------------------------------------- imports
  private browse(accept: string, onFiles: (files: File[]) => void): void {
    const input = el("input", { attrs: { type: "file", accept } }) as HTMLInputElement;
    input.style.display = "none";
    input.addEventListener("change", () => {
      if (input.files && input.files.length) onFiles(Array.from(input.files));
      input.remove();
    });
    document.body.append(input);
    input.click();
  }

  private wireGlobalEvents(): void {
    window.addEventListener("dragenter", (e) => {
      e.preventDefault();
      this.dragCounter++;
      this.dropOverlay.classList.add("active");
    });
    window.addEventListener("dragover", (e) => e.preventDefault());
    window.addEventListener("dragleave", (e) => {
      e.preventDefault();
      this.dragCounter = Math.max(0, this.dragCounter - 1);
      if (this.dragCounter === 0) this.dropOverlay.classList.remove("active");
    });
    window.addEventListener("drop", (e) => {
      e.preventDefault();
      this.dragCounter = 0;
      this.dropOverlay.classList.remove("active");
      const files = e.dataTransfer ? Array.from(e.dataTransfer.files) : [];
      if (files.length) this.handleFiles(files);
    });
    window.addEventListener("paste", (e) => {
      const items = e.clipboardData ? Array.from(e.clipboardData.items) : [];
      for (const item of items) {
        if (item.type.startsWith("image/")) {
          const file = item.getAsFile();
          if (file) {
            this.handleFiles([file]);
            return;
          }
        }
      }
    });
    window.addEventListener("keydown", (e) => {
      const t = e.target as HTMLElement | null;
      const typing =
        !!t &&
        (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.tagName === "SELECT" || t.isContentEditable);
      // A modal dialog owns its own Escape/Tab handling.
      const inModal = !!t && !!t.closest(".modal-backdrop");
      if (e.ctrlKey || e.metaKey) {
        if (e.key === "0") {
          e.preventDefault();
          this.canvasView.fit();
        } else if (e.key === "1") {
          e.preventDefault();
          this.canvasView.resetView();
        }
        return;
      }
      if (inModal) return;
      if (e.key === "?" || (e.key === "/" && e.shiftKey)) {
        if (typing) return;
        e.preventDefault();
        openHelpModal(document.activeElement instanceof HTMLElement ? document.activeElement : null);
      } else if ((e.key === "c" || e.key === "C") && !typing) {
        if (this.compareBtn && !this.compareBtn.disabled) {
          e.preventDefault();
          this.toggleCompare();
        }
      } else if (e.key === "Escape") {
        // Escape closes the off-canvas drawer (landscape phone) and restores
        // focus to the toggle. The always-visible sheet/dock stay put.
        if (this.layoutTier === "drawer" && this.panelEl.classList.contains("open")) {
          this.setPanelOpen(false);
        }
      }
    });

    // Free the workers + canvas when the page is being torn down.
    window.addEventListener("pagehide", () => this.dispose());
  }

  private dispose(): void {
    for (const q of this.layoutMedia) q.removeEventListener("change", this.onLayoutMediaChange);
    // T7 adds dispose() to both clients; tolerate its absence during in-flight
    // integration by feature-detecting the method (cancel() is the fallback).
    disposeClient(this.pixelate);
    disposeClient(this.photoClient);
    try {
      this.canvasView.destroy();
    } catch {
      /* best-effort teardown */
    }
  }

  private handleFiles(files: File[]): void {
    const file = files[0];
    if (!file) return;
    const name = file.name;
    const isProject = /\.lbrn2?$/i.test(name);
    const isSvg = /\.svg$/i.test(name) || file.type === "image/svg+xml";
    const isRaster = /^image\//.test(file.type) || /\.(png|jpe?g|gif|webp|bmp)$/i.test(name);
    const tab = this.store.get().activeTab;
    if (isProject) {
      this.importVectorFile(file);
    } else if (isSvg) {
      if (tab === "photo") this.loadPhotoImage(file);
      else this.importVectorFile(file);
    } else if (isRaster) {
      if (tab === "vector" && this.store.get().photoFill.enabled) this.loadPhotoFillImage(file);
      else this.loadPhotoImage(file);
    } else {
      toast("warn", "Unsupported file: " + name);
    }
  }

  private async importVectorFile(file: File): Promise<void> {
    try {
      const bytes = await fileToBytes(file);
      let doc: VectorDocument;
      if (/\.svg$/i.test(file.name) || file.type === "image/svg+xml") {
        doc = parseSvgVector(decodeTextSniffed(bytes), file.name);
      } else {
        doc = await parseLbrn(bytes, file.name);
      }
      const importable = doc.layers.filter((l) => l.shapeCount > 0);
      const selected = new Set<number>((importable.length ? importable : doc.layers).map((l) => l.index));
      this.shownWarnings.clear();
      this.vectorGeoms = [];
      this.vectorGeomsKey = "";
      this.store.set({
        doc,
        docFileName: doc.fileName,
        selectedLayers: selected,
        activeTab: "vector",
        vectorResult: null,
        vectorFillResult: null,
        vectorTruncated: false,
        vectorFullSignature: null,
        status: doc.layers.length
          ? `Loaded ${doc.fileName} — ${doc.layers.length} layer${doc.layers.length === 1 ? "" : "s"}.`
          : `Loaded ${doc.fileName}, but no usable layers were found.`,
      });
      savePersisted(this.store.get());
      if (!doc.layers.length) toast("warn", "No importable layers found in " + doc.fileName);
      else toast("success", "Loaded " + doc.fileName);
      this.scheduleVectorPreview();
    } catch (err) {
      toast("error", "Could not open " + file.name + ": " + errMsg(err));
    }
  }

  private async loadPhotoImage(file: File | undefined): Promise<void> {
    if (!file) return;
    try {
      const img = await decodeImageFile(file, file.name);
      this.store.set({
        photoImage: img,
        activeTab: "photo",
        photoResult: null,
        photoResultSignature: null,
        status: `Loaded image ${img.name} (${img.width}×${img.height}).`,
      });
      toast("success", "Loaded image " + img.name);
      this.schedulePhotoPreview();
    } catch (err) {
      toast("error", "Could not load image " + file.name + ": " + errMsg(err));
    }
  }

  private async loadPhotoFillImage(file: File | undefined): Promise<void> {
    if (!file) return;
    try {
      const img = await decodeImageFile(file, file.name);
      this.store.set({
        photoFillImage: img,
        photoFill: { ...this.store.get().photoFill, enabled: true },
        vectorFullSignature: null,
        status: `Photo-fill image ${img.name} loaded.`,
      });
      savePersisted(this.store.get());
      toast("success", "Photo-fill image loaded");
      this.scheduleVectorPreview();
    } catch (err) {
      toast("error", "Could not load image " + file.name + ": " + errMsg(err));
    }
  }

  // ------------------------------------------------------------ generation
  scheduleVectorPreview(): void {
    if (this.previewTimer !== null) clearTimeout(this.previewTimer);
    this.previewTimer = window.setTimeout(() => {
      this.previewTimer = null;
      void this.runVectorPreview();
    }, DEBOUNCE_MS);
  }

  private ensureVectorGeoms(s: AppState): boolean {
    if (!s.doc) return false;
    const key = (s.docFileName ?? "") + "|" + [...s.selectedLayers].sort((a, b) => a - b).join(",") + "|" + s.pixel.curveSteps;
    if (key === this.vectorGeomsKey && this.vectorGeoms.length >= 0 && this.vectorGeomsKey !== "") return true;
    const { geoms, warnings } = s.doc.extractGeometry(s.selectedLayers, s.pixel.curveSteps);
    this.vectorGeoms = geoms;
    this.vectorGeomsKey = key;
    for (const w of warnings) {
      if (!this.shownWarnings.has(w.message)) {
        this.shownWarnings.add(w.message);
        toast(w.kind === "skip" ? "warn" : "warn", w.message + (w.count > 1 ? ` (×${w.count})` : ""));
      }
    }
    return true;
  }

  private buildPhotoFillOption(s: AppState): { settings: PhotoFillSettings; image: RawImage } | undefined {
    if (s.photoFill.enabled && s.photoFillImage) return { settings: s.photoFill, image: s.photoFillImage };
    return undefined;
  }

  private async runVectorPreview(): Promise<void> {
    const s = this.store.get();
    if (!s.doc || s.selectedLayers.size === 0) {
      this.vectorGeoms = [];
      this.store.set({ vectorResult: null, vectorFillResult: null, vectorTruncated: false });
      return;
    }
    if (!this.ensureVectorGeoms(s)) return;
    try {
      const { result, photoFill } = await this.pixelate.run(this.vectorGeoms, s.selectedLayers, safePixel(s.pixel), {
        softLimit: true,
        maxPixelsOverride: Math.max(100, Math.round(s.livePreviewCap || 100)),
        photoFill: this.buildPhotoFillOption(s),
      });
      this.store.set({
        vectorResult: result,
        vectorFillResult: photoFill ?? null,
        vectorTruncated: result.truncated,
        status: result.truncated
          ? `Preview showing first ${result.pixels.length.toLocaleString()} dots — click Generate for the full exportable set.`
          : `Preview: ${result.pixels.length.toLocaleString()} dots${result.clippedCount ? `, ${result.clippedCount.toLocaleString()} clipped` : ""}.`,
      });
    } catch (err) {
      if (!isSuperseded(err)) toast("error", "Preview failed: " + errMsg(err));
    }
  }

  private async generateVector(): Promise<void> {
    const s = this.store.get();
    if (s.busy) return; // a full generate is already running
    if (!s.doc) {
      toast("warn", "Open a file first.");
      return;
    }
    if (s.selectedLayers.size === 0) {
      toast("warn", "Select at least one layer.");
      return;
    }
    if (!this.ensureVectorGeoms(s)) return;
    // A pending debounced live preview would supersede this full generate.
    if (this.previewTimer !== null) {
      clearTimeout(this.previewTimer);
      this.previewTimer = null;
    }
    // Signature of the settings this job actually runs with — captured at
    // dispatch so a mid-flight setting change can never gate stale output.
    const sig = currentVectorSignature(s);
    this.store.set({ busy: true });
    this.progress.show("Generating dots");
    try {
      const { result, photoFill } = await this.pixelate.run(this.vectorGeoms, s.selectedLayers, safePixel(s.pixel), {
        softLimit: false,
        photoFill: this.buildPhotoFillOption(s),
        onProgress: (p: JobProgress) => this.progress.update(p.stage, p.fraction, p.pixelCount.toLocaleString() + " dots"),
      });
      if (result.truncated) {
        this.store.set({
          vectorResult: result,
          vectorFillResult: photoFill ?? null,
          vectorTruncated: true,
          vectorFullSignature: null,
          busy: false,
          status: `Hit the ${s.pixel.maxPixels.toLocaleString()}-pixel safety cap. Raise it or coarsen spacing, then generate again.`,
        });
        toast("warn", "Generation hit the max-pixels safety cap — exports stay disabled.");
      } else {
        this.store.set({
          vectorResult: result,
          vectorFillResult: photoFill ?? null,
          vectorTruncated: false,
          vectorFullSignature: sig,
          busy: false,
          status: `Ready: ${result.pixels.length.toLocaleString()} dots${result.clippedCount ? `, ${result.clippedCount.toLocaleString()} clipped edge pixels` : ""}. Exports enabled.`,
        });
        toast("success", "Full set generated — exports enabled.");
      }
    } catch (err) {
      this.store.set({ busy: false });
      if (!isSuperseded(err) && !isCancelled(err)) toast("error", "Generation failed: " + errMsg(err));
    } finally {
      this.progress.hide();
    }
  }

  schedulePhotoPreview(): void {
    if (this.photoTimer !== null) clearTimeout(this.photoTimer);
    this.photoTimer = window.setTimeout(() => {
      this.photoTimer = null;
      void this.runPhoto();
    }, DEBOUNCE_MS);
  }

  private async runPhoto(): Promise<void> {
    const s = this.store.get();
    if (!s.photoImage) {
      this.store.set({ photoResult: null, photoResultSignature: null });
      return;
    }
    // Capture at dispatch — see generateVector.
    const sig = currentPhotoSignature(s);
    this.store.set({ status: "Building photo color layers…" });
    try {
      const result = await this.photoClient.run(s.photoImage, safePhoto(s.photo), {
        onProgress: (p: JobProgress) => {
          if (p.fraction >= 0) this.store.set({ status: `${p.stage}… ${Math.round(p.fraction * 100)}%` });
        },
      });
      this.store.set({
        photoResult: result,
        photoResultSignature: sig,
        status: `Photo layers: ${result.palette.length} colors, ${result.visibleCount.toLocaleString()} dots on a ${result.cols}×${result.rows} grid.`,
      });
    } catch (err) {
      if (!isSuperseded(err) && !isCancelled(err)) toast("error", "Photo generation failed: " + errMsg(err));
    }
  }

  private cancel(): void {
    this.pixelate.cancel();
    this.photoClient.cancel();
    this.progress.hide();
    this.store.set({ busy: false, status: "Cancelled." });
  }

  // ---------------------------------------------------------------- exports
  private collectCutParams(s: AppState, layers: Iterable<number>): Record<number, CutParams> | undefined {
    const out: Record<number, CutParams> = {};
    let any = false;
    for (const layer of layers) {
      const cp = s.cutParams[layer];
      if (cp) {
        out[layer] = cp;
        any = true;
      }
    }
    return any ? out : undefined;
  }

  private buildVectorContext(s: AppState): VectorExportContext {
    const usingFill = s.photoFill.enabled && !!s.vectorFillResult;
    const pixels = usingFill ? s.vectorFillResult!.pixels : s.vectorResult!.pixels;
    const layers: Record<number, LayerInfo> = {};
    if (s.doc) for (const li of s.doc.layers) layers[li.index] = li;
    const layerSet = new Set(pixels.map((p) => p.layer));
    return {
      pixels,
      settings: s.pixel,
      layers,
      photoPalette: usingFill ? s.vectorFillResult!.palette : undefined,
      cutParams: this.collectCutParams(s, layerSet),
    };
  }

  private async exportVectorSvg(): Promise<void> {
    const s = this.store.get();
    if (!vectorExportReady(s)) return void toast("warn", "Generate the full set before exporting.");
    try {
      const svg = buildVectorSvg(this.buildVectorContext(s));
      const name = deriveFileName(s.docFileName ?? "pixelated", "_pixelated.svg");
      await this.save(svg, name, "image/svg+xml", ".svg", "SVG");
    } catch (err) {
      toast("error", "SVG export failed: " + errMsg(err));
    }
  }

  private async exportVectorLbrn(): Promise<void> {
    const s = this.store.get();
    if (!vectorExportReady(s) || !s.doc) return void toast("warn", "Generate the full set before exporting.");
    try {
      const xml = exportLbrnCopy(s.doc.sourceText, this.buildVectorContext(s), {
        replaceOriginal: s.replaceOriginal,
        selectedLayers: s.selectedLayers,
      });
      const name = deriveFileName(s.docFileName ?? "pixelated", "_pixelated.lbrn2");
      await this.save(xml, name, "application/xml", ".lbrn2", "LightBurn copy");
    } catch (err) {
      toast("error", "LightBurn export failed: " + errMsg(err));
    }
  }

  private vectorFillPhotoResult(s: AppState): PhotoResult | null {
    if (!(s.photoFill.enabled && s.vectorFillResult && s.photoFillImage)) return null;
    return buildPhotoResultFromFill(s.vectorFillResult, s.pixel, s.photoFill, {
      name: s.photoFillImage.name,
      width: s.photoFillImage.width,
      height: s.photoFillImage.height,
    });
  }

  private async exportVectorPaletteCsv(): Promise<void> {
    const s = this.store.get();
    const pr = this.vectorFillPhotoResult(s);
    if (!pr) return void toast("warn", "Palette CSV is available when photo fill is active.");
    try {
      const cutParams = this.collectCutParams(s, pr.palette.map((p) => p.layerIndex));
      const csv = buildPaletteCsv(pr, cutParams);
      await this.save(csv, deriveFileName(s.docFileName ?? "photo-fill", "_palette.csv"), "text/csv", ".csv", "Palette CSV");
    } catch (err) {
      toast("error", "CSV export failed: " + errMsg(err));
    }
  }

  private exportVectorReport(): void {
    const s = this.store.get();
    const pr = this.vectorFillPhotoResult(s);
    if (!pr) return void toast("warn", "The report is available when photo fill is active.");
    this.openReport(pr, this.collectCutParams(s, pr.palette.map((p) => p.layerIndex)));
  }

  private async exportPhotoSvg(): Promise<void> {
    const s = this.store.get();
    if (!photoExportReady(s) || !s.photoResult) return void toast("warn", "Generate photo layers first.");
    try {
      const svg = buildPhotoSvg(s.photoResult, { optimizeSquareRuns: s.photo.optimizeSquareRuns });
      await this.save(svg, deriveFileName(s.photoResult.sourceName, "_layers.svg"), "image/svg+xml", ".svg", "Layered SVG");
    } catch (err) {
      toast("error", "SVG export failed: " + errMsg(err));
    }
  }

  private async exportPhotoCsv(): Promise<void> {
    const s = this.store.get();
    if (!photoExportReady(s) || !s.photoResult) return void toast("warn", "Generate photo layers first.");
    try {
      const cutParams = this.collectCutParams(s, s.photoResult.palette.map((p) => p.layerIndex));
      const csv = buildPaletteCsv(s.photoResult, cutParams);
      await this.save(csv, deriveFileName(s.photoResult.sourceName, "_palette.csv"), "text/csv", ".csv", "Palette CSV");
    } catch (err) {
      toast("error", "CSV export failed: " + errMsg(err));
    }
  }

  private exportPhotoReport(): void {
    const s = this.store.get();
    if (!photoExportReady(s) || !s.photoResult) return void toast("warn", "Generate photo layers first.");
    this.openReport(s.photoResult, this.collectCutParams(s, s.photoResult.palette.map((p) => p.layerIndex)));
  }

  private async exportPhotoLbrn(): Promise<void> {
    const s = this.store.get();
    if (!photoExportReady(s) || !s.photoResult) return void toast("warn", "Generate photo layers first.");
    try {
      const pixels = photoResultToPixels(s.photoResult);
      const settings: PixelSettings = {
        ...DEFAULT_PIXEL_SETTINGS,
        pixelSize: s.photoResult.pixelSize,
        spacingX: s.photoResult.spacingX,
        spacingY: s.photoResult.spacingY,
        shape: s.photoResult.shape,
        edgeMode: "none",
      };
      const ctx: VectorExportContext = {
        pixels,
        settings,
        layers: {},
        photoPalette: s.photoResult.palette,
        cutParams: this.collectCutParams(s, s.photoResult.palette.map((p) => p.layerIndex)),
      };
      const xml = exportLbrnStandalone(ctx);
      await this.save(xml, deriveFileName(s.photoResult.sourceName, "_layers.lbrn2"), "application/xml", ".lbrn2", "LightBurn project");
    } catch (err) {
      toast("error", "LightBurn export failed: " + errMsg(err));
    }
  }

  private async exportTestGrid(): Promise<void> {
    const s = this.store.get();
    if (!s.testGrid.rowValues.length || !s.testGrid.colValues.length) {
      return void toast("warn", "Add at least one row value and one column value.");
    }
    try {
      const xml = buildTestGrid(s.testGrid);
      await this.save(xml, "mopa-test-grid.lbrn2", "application/xml", ".lbrn2", "Test grid");
    } catch (err) {
      toast("error", "Test-grid export failed: " + errMsg(err));
    }
  }

  /** One-click bundle: SVG + CSV + .lbrn2 + report as a single .zip download. */
  private async exportAll(): Promise<void> {
    const s = this.store.get();
    try {
      if (s.activeTab === "photo") {
        if (!photoExportReady(s) || !s.photoResult) return void toast("warn", "Generate photo layers first.");
        const result = s.photoResult;
        const cutParams = this.collectCutParams(s, result.palette.map((p) => p.layerIndex));
        const pixels = photoResultToPixels(result);
        const lbrnCtx: VectorExportContext = {
          pixels,
          settings: { ...DEFAULT_PIXEL_SETTINGS, pixelSize: result.pixelSize, spacingX: result.spacingX, spacingY: result.spacingY, shape: result.shape, edgeMode: "none" },
          layers: {},
          photoPalette: result.palette,
          cutParams,
        };
        const ok = await exportAllPhotoBundle(result, {
          optimizeSquareRuns: s.photo.optimizeSquareRuns,
          cutParams,
          lbrnCtx,
          context: { machine: s.reportMachine, material: s.reportMaterial },
        });
        if (ok) toast("success", "Bundle saved (.zip)");
      } else {
        if (!vectorExportReady(s) || !s.doc) return void toast("warn", "Generate the full set before exporting.");
        const ctx = this.buildVectorContext(s);
        const photoResult = this.vectorFillPhotoResult(s) ?? undefined;
        const ok = await exportAllVectorBundle(ctx, {
          sourceName: s.docFileName ?? "pixelated",
          originalXml: s.doc.sourceText,
          replaceOriginal: s.replaceOriginal,
          selectedLayers: s.selectedLayers,
          photoResult,
          cutParams: ctx.cutParams,
          context: { machine: s.reportMachine, material: s.reportMaterial },
        });
        if (ok) toast("success", "Bundle saved (.zip)");
      }
    } catch (err) {
      toast("error", "Export all failed: " + errMsg(err));
    }
  }

  private openReport(result: PhotoResult, cutParams?: Record<number, CutParams>): void {
    try {
      const s = this.store.get();
      const html = buildPaletteReportHtml(result, { machine: s.reportMachine, material: s.reportMaterial }, cutParams);
      const blob = new Blob([html], { type: "text/html" });
      const url = URL.createObjectURL(blob);
      const win = window.open(url, "_blank");
      if (!win) {
        void this.save(html, deriveFileName(result.sourceName, "_report.html"), "text/html", ".html", "Report");
        toast("info", "Pop-up blocked — the report was downloaded instead.");
      }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      toast("error", "Report failed: " + errMsg(err));
    }
  }

  private async save(text: string, name: string, mime: string, ext: string, label: string): Promise<void> {
    const ok = await saveTextFile(text, name, mime, ext);
    if (ok) toast("success", label + " saved");
  }
}

// ===========================================================================
// Bootstrapping + small utilities.
// ===========================================================================

/** Floor geometry-critical fields so a transiently-typed 0 can't spin a worker. */
function safePixel(p: PixelSettings): PixelSettings {
  return {
    ...p,
    pixelSize: Math.max(0.001, p.pixelSize || 0.001),
    spacingX: Math.max(0.001, p.spacingX || 0.001),
    spacingY: Math.max(0.001, p.spacingY || 0.001),
    curveSteps: Math.max(3, Math.min(96, Math.round(p.curveSteps || 16))),
    maxPixels: Math.max(1, Math.round(p.maxPixels || 1)),
  };
}

function safePhoto(p: PhotoSettings): PhotoSettings {
  return {
    ...p,
    maxLayers: Math.max(1, Math.min(30, Math.round(p.maxLayers || 1))),
    outputWidthMm: Math.max(1, p.outputWidthMm || 1),
    pixelSize: Math.max(0.001, p.pixelSize || 0.001),
    spacingX: Math.max(0.001, p.spacingX || 0.001),
    spacingY: Math.max(0.001, p.spacingY || 0.001),
    maxPixels: Math.max(1, Math.round(p.maxPixels || 1)),
  };
}

const objIds = new WeakMap<object, number>();
let objSeq = 0;
function idOf(o: object | null | undefined): number {
  if (!o) return 0;
  let id = objIds.get(o);
  if (id === undefined) {
    id = ++objSeq;
    objIds.set(o, id);
  }
  return id;
}

/**
 * Tear down a worker client. Prefers a dispose()/terminate() method (T7 adds
 * dispose() to both clients); falls back to cancel(), which every client has.
 */
function disposeClient(client: { cancel(): void }): void {
  try {
    const anyClient = client as { dispose?: () => void; terminate?: () => void; cancel: () => void };
    if (typeof anyClient.dispose === "function") anyClient.dispose();
    else if (typeof anyClient.terminate === "function") anyClient.terminate();
    else anyClient.cancel();
  } catch {
    /* best-effort teardown */
  }
}

function errMsg(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}
function isSuperseded(err: unknown): boolean {
  return err instanceof Error && /superseded/i.test(err.message);
}
function isCancelled(err: unknown): boolean {
  return err instanceof Error && /cancel/i.test(err.message);
}

/**
 * Pin the shell height to the real viewport height. `100dvh` mis-resolves on
 * some browsers (leaving the shell filling only the top part of the window and
 * black below); syncing --app-vh to window.innerHeight is authoritative and
 * cheap. Runs on resize + orientation change.
 */
function installViewportHeightSync(): void {
  if (typeof window === "undefined") return;
  const apply = (): void => {
    const h = window.innerHeight;
    if (h > 0) document.documentElement.style.setProperty("--app-vh", h + "px");
  };
  apply();
  window.addEventListener("resize", apply, { passive: true });
  window.addEventListener("orientationchange", apply, { passive: true });
}

export function mountApp(root: HTMLElement): void {
  installViewportHeightSync();
  new AppController(root);
}
